import { createUseStyles } from 'react-jss';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, useTheme } from '@mui/material';

//import { Button } from '../Button';
import type { LcpnTheme } from '../../styles/theme';
import { useNavigationBarData } from '../NavigationBar/NavigationBarProvider';
import { usePoiFilterOptions } from '../../map-data/use-filter-options';
import { useHeatMapFilterOptions } from '../../map-data/use-heat-map-filter-options';
import type { HeatMapRange } from '../../map-data/map-data-types';

import { RangeFilter } from './RangeFilter';
import { LozengeSelectFilter } from './LozengeSelectFilter';
import { MultiSelectFilter } from './MultiSelectFilter';
import { SelectFilter } from './SelectFilter';
import type { FilterConfig, FilterWidth } from './filter-types';

const useStyles = createUseStyles((theme: LcpnTheme) => ({
  filterContainer: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: `calc(100vh - ${theme.spacing(30)})`,
    overflowY: 'scroll',
  },

  filter: {
    backgroundColor: '#E3EBE9',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    '& > div:last-child > div > div:last-child': {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'flex-start',
      '& > button': {
        flexGrow: 1,
        minHeight: theme.spacing(7),
      },
    },
  },

  item: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },

  itemFull: {
    width: '100%',
    borderBottom: `1px solid ${theme.colors.white}`,
    marginBottom: -1,
  },

  itemAuto: {
    flex: '0 1 33.333%',
    minWidth: theme.spacing(50),
    borderBottom: `1px solid ${theme.colors.white}`,
    marginBottom: -1,
    [`@media screen and (min-width: ${theme.breakPoints.widescreen}px)`]: {
      flex: '0 1 25%',
    },
    [`@media screen and (max-width: ${theme.breakPoints.desktop}px)`]: {
      flex: '0 1 50%',
    },
    [`@media screen and (max-width: ${theme.breakPoints.tablet}px)`]: {
      width: '100%',
      flex: 'none',
      minWidth: 'unset',
    },
  },

  itemHalf: {
    flex: '0 1 50%',
    minWidth: theme.spacing(75),
    borderBottom: `1px solid ${theme.colors.white}`,
    marginBottom: -1,
    [`@media screen and (max-width: ${theme.breakPoints.tablet}px)`]: {
      width: '100%',
      flex: 'none',
      minWidth: 'unset',
    },
  },

  group: {
    display: 'flex',
    alignItems: 'center',
  },

  row: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    borderBottom: `1px solid ${theme.colors.white}`,
  },

  clearFilter: {
    display: 'flex',
    justifyContent: 'flex-end',
    backgroundColor: 'transparent',
    alignItems: 'center',
    padding: theme.spacing(4, 2, 6, 0),
    marginLeft: 'auto',
    '& > button': {
      backgroundColor: theme.colors['green-100'],
      color: theme.colors.white,
    },
    marginRight: theme.spacing(4),
  },

  [`@media screen and (min-width: ${theme.breakPoints.tablet}px)`]: {
    filterContainer: {
      '&&': {
        maxHeight: `calc(100vh - ${theme.spacing(37)})`,
      },
    },
    filter: {
      '&&': {
        '& > div:last-child > div:first-child': {
          display: 'flex',
          alignItems: 'center',
        },
      },
    },
    clearFilter: {
      '&&': {
        padding: theme.spacing(4, 0),
      },
    },
  },

  [`@media screen and (min-width: ${theme.breakPoints.widescreen}px)`]: {
    clearFilter: {
      '&&': {
        padding: theme.spacing(6, 0),
        marginLeft: 'auto',
      },
    },
  },

  [`@media screen and (min-height: 677px)`]: {
    filterContainer: {
      '&&': {
        overflowY: 'auto',
      },
    },
    filter: {
      '&&': {
        overflowY: 'auto',
      },
    },
  },
}));

// type guard to remove TS error if generic is not instantiated with an object
const isObject = (obj: unknown): obj is Record<string, unknown> =>
  String(obj) === '[object Object]' && !Array.isArray(obj) && obj !== null;

const getWidthClass = (
  classes: Record<string, string>,
  width: FilterWidth = 'auto',
) => {
  switch (width) {
    case 'full': {
      return classes.itemFull;
    }
    case 'half': {
      return classes.itemHalf;
    }
    default: {
      return classes.itemAuto;
    }
  }
};

type Props = {
  filterConfig: FilterConfig;
  resetFilterOptions: VoidFunction;
  /** Current view mode - used to filter out configs that don't apply to this mode */
  viewMode?: HeatMapRange;
};

export const Filter: FC<Props> = ({
  filterConfig,
  resetFilterOptions,
  viewMode,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  const { filterOptions: currentFiltersPoi } = usePoiFilterOptions();
  const { filterOptions: currentFiltersHeatMap } = useHeatMapFilterOptions();

  const { selectedNavigationItem, selectedExperience } = useNavigationBarData();

  if (selectedNavigationItem !== 'filter') {
    return null;
  }

  // Use the appropriate filter options based on the current experience
  const currentFilters =
    selectedExperience === 'heatMap'
      ? currentFiltersHeatMap
      : currentFiltersPoi;

  // Filter the config based on viewMode - if viewModes is undefined, show in all modes
  const filteredConfig = filterConfig
    .map(optionGroup =>
      optionGroup.filter(
        config =>
          !config.viewModes || !viewMode || config.viewModes.includes(viewMode),
      ),
    )
    .filter(optionGroup => optionGroup.length > 0);

  return (
    <div className={classes.filterContainer}>
      <div className={classes.filter}>
        {filteredConfig.map((optionGroup, rowIndex) => {
          return (
            <div key={rowIndex} className={classes.row}>
              {optionGroup.map(filterConfig => {
                const widthClass = getWidthClass(classes, filterConfig.width);

                if (filterConfig.filterType === 'multiChoice') {
                  return (
                    <div key={filterConfig.key} className={widthClass}>
                      <LozengeSelectFilter
                        filterConfig={filterConfig}
                        selectedValue={
                          isObject(currentFilters)
                            ? (currentFilters[
                                filterConfig.key as keyof typeof currentFilters
                              ] as string | undefined)
                            : undefined
                        }
                        isMultiSelect={false}
                        useOutlineStyle={false}
                      />
                    </div>
                  );
                }

                if (filterConfig.filterType === 'multiSelect') {
                  return (
                    <div key={filterConfig.key} className={widthClass}>
                      <MultiSelectFilter
                        multiSelectFilterConfig={filterConfig}
                        selectedValues={
                          isObject(currentFilters)
                            ? Array.isArray(
                                currentFilters[
                                  filterConfig.key as keyof typeof currentFilters
                                ],
                              )
                              ? (currentFilters[
                                  filterConfig.key as keyof typeof currentFilters
                                ] as string[])
                              : []
                            : []
                        }
                      />
                    </div>
                  );
                }

                if (filterConfig.filterType === 'multiChoiceToggle') {
                  return (
                    <div key={filterConfig.key} className={widthClass}>
                      <LozengeSelectFilter
                        filterConfig={filterConfig}
                        selectedValues={
                          isObject(currentFilters)
                            ? Array.isArray(
                                currentFilters[
                                  filterConfig.key as keyof typeof currentFilters
                                ],
                              )
                              ? (currentFilters[
                                  filterConfig.key as keyof typeof currentFilters
                                ] as string[])
                              : []
                            : []
                        }
                        isMultiSelect={true}
                        useOutlineStyle={true}
                        clearValue={filterConfig.clearValue}
                      />
                    </div>
                  );
                }

                if (filterConfig.filterType === 'range') {
                  let rangeFinish: number | undefined;
                  let rangeStart: number | undefined;

                  if (selectedExperience === 'pinMap') {
                    rangeFinish = currentFiltersPoi.endFinish;
                    rangeStart = currentFiltersPoi.startFinish;
                  } else if (selectedExperience === 'heatMap') {
                    rangeFinish = currentFiltersHeatMap.nccpYearMax;
                    rangeStart = filterConfig.fixedStart
                      ? filterConfig.rangeFrom.min
                      : undefined;
                  }

                  return (
                    <div key={filterConfig.key} className={widthClass}>
                      <RangeFilter
                        rangeFilterConfig={filterConfig}
                        rangeFinish={rangeFinish}
                        rangeStart={rangeStart}
                      />
                    </div>
                  );
                }

                if (filterConfig.filterType === 'select') {
                  // We have a select filter that is combining multiple filterable properties. We therefore
                  // have to attempt to find the correct property that is currently filtered by the various keys
                  const getSelectedValue = (): string => {
                    if (!isObject(currentFilters)) return '';

                    const filterType = filterConfig.key.find(
                      k => currentFilters[k as keyof typeof currentFilters],
                    );

                    if (!filterType) return '';

                    return (
                      (currentFilters[
                        filterType as keyof typeof currentFilters
                      ] as string) ?? ''
                    );
                  };

                  return (
                    <div key={filterConfig.key[0]} className={widthClass}>
                      <SelectFilter
                        selectFilterConfig={filterConfig}
                        selectedValue={getSelectedValue()}
                      />
                    </div>
                  );
                }

                return null;
              })}
            </div>
          );
        })}
        <div className={classes.clearFilter}>
          <Button
            onClick={() => resetFilterOptions()}
            sx={{
              backgroundColor: `${theme.palette.grey['300']} !important`,
              color: `${theme.palette.grey[900]} !important`,
            }}
          >
            {t('filters.clearFilters')}
          </Button>
        </div>
      </div>
    </div>
  );
};
